import { AxiosRequestConfig } from "axios";
import { Packet } from "../Interfaces/commonDtos";
import { DropdownItem } from "../Interfaces/dropdownInterfaces";
import {
  IAddEditMarketmakingTask,
  IMarketmakingConfig,
  IMarketmakingOrderbookPairResp,
  IMarketmakingOrderTableReport,
  IMarketmakingTradeTableSearchParams,
  IMarketmakingProgram,
  IMarketmakingTaskSearchParam,
  IMarketmakingTaskTableReport,
  IMarketmakingTradeTableReport,
  IMarketmakingTriggerTableReport,
  IMarketmakingSetProgramBody,
  IMarketmakingOrderTableSearchParams,
  IGetPreviewOfferResp,
  IMarketmakingPredefinedProgram,
} from "../Interfaces/marketmaking";
import { ErrorTableReport } from "../Interfaces/orderInterfaces";
import { Report } from "../Interfaces/TableInterfaces";
import {
  deleteRequest,
  getRequest,
  getTable,
  patchRequest,
  postRequest,
  putRequest,
} from "./requestTemplates";

export const getMarketmakingTasksTable = (
  searchParam: IMarketmakingTaskSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IMarketmakingTaskTableReport>> =>
  getTable<IMarketmakingTaskSearchParam, IMarketmakingTaskTableReport>(
    "/v1/marketMaking/taskTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getMarketmakingOrderbookPair = (
  taskId: string
): Promise<IMarketmakingOrderbookPairResp> => {
  const urlSearchParam = new URLSearchParams();
  let urlStr = "/v1/marketMaking/GetOrderBook";
  if (taskId) {
    urlSearchParam.append("TaskId", taskId.toString());
    urlStr += "?" + urlSearchParam;
  }
  return getRequest(urlStr);
};

export const addMarketmakingTask = (
  formValues: IAddEditMarketmakingTask
): Promise<Packet<boolean>> =>
  postRequest("/v1/MarketMaking/addTask", formValues);

export const editMarketmakingTask = (
  formValues: IAddEditMarketmakingTask
): Promise<Packet<boolean>> =>
  putRequest("/v1/marketMaking/editTask", formValues);

export const getMarketmakingOrderTable = (
  searchParam: IMarketmakingOrderTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IMarketmakingOrderTableReport>> =>
  getTable<IMarketmakingOrderTableSearchParams, IMarketmakingOrderTableReport>(
    "/v1/marketMaking/OrderTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getMarketmakingTradeTable = (
  searchParam: IMarketmakingTradeTableSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<IMarketmakingTradeTableReport>> =>
  getTable<IMarketmakingTradeTableSearchParams, IMarketmakingTradeTableReport>(
    "/v1/marketMaking/TradeTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const getMarketmakingErrorsTable = (
  searchParam: IMarketmakingTradeTableSearchParams,
  offset: number | null,
  pageSize: number | null
): Promise<Report<ErrorTableReport>> =>
  getTable<IMarketmakingTradeTableSearchParams, ErrorTableReport>(
    "/v1/marketMaking/ErrorTable",
    searchParam,
    offset,
    pageSize
  );

export const getMarketmakingTriggerTable = (
  searchParam: { taskId: number },
  offset: number | null,
  pageSize: number | null
): Promise<Report<IMarketmakingTriggerTableReport>> =>
  getTable<{ taskId: number }, IMarketmakingTriggerTableReport>(
    "/v1/MarketMaking/triggerTable",
    searchParam,
    offset,
    pageSize
  );

export const getIVRefTable = (
  searchParam: {},
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig
): Promise<Report<any>> =>
  getTable<{}, any>(
    "/v1/marketMaking/getIVRefTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions
  );

export const cancelMarketmakingOrder = (id: number): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/marketMaking/cancel?OrderId=${id}`);

export const getMarketmakingConfig = (): Promise<IMarketmakingConfig> =>
  getRequest("/v1/marketMaking/GetConfig");

export const setMarketmakingConfig = (
  body: IMarketmakingConfig
): Promise<Packet<boolean>> => putRequest("/v1/marketMaking/setConfig", body);

export const getMarketmakingProgram = (
  taskId: number
): Promise<IMarketmakingProgram> =>
  getRequest(`/v1/marketMaking/getProgram?taskId=${taskId}`);

export const setMarketmakingProgram = (
  body: IMarketmakingSetProgramBody
): Promise<Packet<boolean>> => putRequest("/v1/marketMaking/setProgram", body);

export const setMarketmakingPredefinedProgram = (
  body: IMarketmakingPredefinedProgram
): Promise<Packet<boolean>> =>
  postRequest("/v1/marketMaking/AddPreDefinedProgram", body);

export const getMarketmakingPredefinedPrograms = (): Promise<
  IMarketmakingPredefinedProgram[]
> => getRequest("/v1/marketMaking/GetPreDefinedPrograms");

export const deleteMarketmakingPredefinedProgram = (
  name: string
): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/marketMaking/DeletePreDefinedProgram?programName=${name}`);

export const getDistinctBaseInstrument = async (): Promise<
  Array<DropdownItem>
> => getRequest("/v1/MarketMaking/DistinctBaseInstrument");

export const resumeMarketmakingTasks = (
  ids: number[]
): Promise<Packet<boolean>> =>
  patchRequest(`/v1/MarketMaking/playTask`, { ids });

export const pauseMarketmakingTasks = (
  ids: number[]
): Promise<Packet<boolean>> =>
  patchRequest(`/v1/MarketMaking/pauseTask`, { ids });

export const resendMarketmakingTasks = (
  ids: number[]
): Promise<Packet<boolean>> =>
  postRequest(`/v1/MarketMaking/resendTask`, { ids });

export const deleteMarketmakingTasks = (
  ids: number[]
): Promise<Packet<boolean>> =>
  deleteRequest("/v1/MarketMaking/deleteTask", { ids });

export const getPreviewOffers = (
  taskId: number
): Promise<IGetPreviewOfferResp> =>
  getRequest(`/v1/MarketMaking/getPreviewOffers?taskId=${taskId}`);
